import React, { useState, useEffect, useRef } from 'react';
import { Download } from 'lucide-react';
import { Track, TelemetryState, DspState } from '../types';
import { tg } from '../services/telegram';
import {
  PrevTapeIcon,
  RewindIcon,
  PlayWedgeIcon,
  PauseBarsIcon,
  FastForwardIcon,
  NextTapeIcon,
} from './icons/HardwareIcons';
import { SoundcloudWaveform } from './SoundcloudWaveform';


interface CassetteDeckProps {
  track: Track | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  telemetry: TelemetryState;
  dsp: DspState;
  onSetBass: (db: number) => void;
  onSetTreble: (db: number) => void;
  onSetBalance: (pan: number) => void;
  onTogglePlay: () => void;
  onSeek: (sec: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onSetVolume: (vol: number) => void;
  onOpenFullPlayer: () => void;
  onSendFlacToBot?: (track: Track) => void;
}

const VFD_SEGMENTS = 20;

interface AnalogKnobProps {
  id: string;
  label: string;
  displayValue: string;
  angle: number;
  currentValue: number;
  onDragChange: (startVal: number, totalDelta: number) => void;
  onStep: () => void;
  onReset: () => void;
}

const AnalogKnob: React.FC<AnalogKnobProps> = ({
  id,
  label,
  displayValue,
  angle,
  currentValue,
  onDragChange,
  onStep,
  onReset,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const pointerIdRef = useRef<number | null>(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const startValRef = useRef(0);
  const hasMovedRef = useRef(false);
  const lastTapRef = useRef(0);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
    pointerIdRef.current = e.pointerId;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    startValRef.current = currentValue;
    hasMovedRef.current = false;
    setIsDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    const dx = e.clientX - startPosRef.current.x;
    const dy = startPosRef.current.y - e.clientY; // moving up is positive
    const dist = Math.hypot(dx, dy);
    if (!hasMovedRef.current && dist > 4) {
      hasMovedRef.current = true;
    }
    if (hasMovedRef.current) {
      const totalDelta = dy + dx;
      onDragChange(startValRef.current, totalDelta);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}
    pointerIdRef.current = null;
    setIsDragging(false);

    if (!hasMovedRef.current) {
      const now = Date.now();
      if (now - lastTapRef.current < 280) {
        onReset();
        tg.haptic('medium');
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
        onStep();
        tg.haptic('light');
      }
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReset();
    tg.haptic('medium');
  };

  return (
    <div
      className="cal-cell"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={handleDoubleClick}
      title={`${label}: ${displayValue} (потяните или нажмите, 2x клик для сброса)`}
    >
      <div
        className={`knob-3d-wrapper ${isDragging ? 'dragging' : ''}`}
        style={{ transform: `rotate(${angle.toFixed(1)}deg)` }}
      >
        <img
          src="/assets/deck/brass-knob-3d.png"
          alt={label}
          className="knob-3d-img"
          draggable={false}
        />
      </div>
      <span className="cal-label">{label}</span>
      <span className="cal-value">{displayValue}</span>
    </div>
  );
};

export const CassetteDeck: React.FC<CassetteDeckProps> = ({
  track,
  isPlaying,
  currentTime,
  duration,
  volume,
  telemetry,
  dsp,
  onSetBass,
  onSetTreble,
  onSetBalance,
  onTogglePlay,
  onSeek,
  onNext,
  onPrev,
  onSetVolume,
  onOpenFullPlayer,
  onSendFlacToBot,
}) => {
  const [isEjected, setIsEjected] = useState(false);
  const [isRecoil, setIsRecoil] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [windMultiplier, setWindMultiplier] = useState(1);


  // Refs for 60fps direct DOM rotation of the 3D raytraced gold reels & tape packs
  const leftReelRef = useRef<HTMLDivElement>(null);
  const rightReelRef = useRef<HTMLDivElement>(null);
  const leftPackRef = useRef<HTMLDivElement>(null);
  const rightPackRef = useRef<HTMLDivElement>(null);
  const angleRef = useRef({ l: 0, r: 24 });
  const currentTrackId = track ? track.id : null;
  const prevTrackIdRef = useRef<string | null>(currentTrackId);

  // Peak-hold memory for 20-segment VFD bargraphs
  const [peaks, setPeaks] = useState({ l: 15, r: 16 });

  const progress = duration > 0 ? Math.max(0, Math.min(1, currentTime / duration)) : 0;

  // Trigger realistic cassette eject + swap animation when track changes
  useEffect(() => {
    if (prevTrackIdRef.current !== currentTrackId) {
      prevTrackIdRef.current = currentTrackId;
      if (currentTrackId) {
        triggerRecoil();
        setIsEjected(true);
        setIsSwapping(true);
        const t1 = setTimeout(() => setIsSwapping(false), 240);
        const t2 = setTimeout(() => setIsEjected(false), 380);
        return () => {
          clearTimeout(t1);
          clearTimeout(t2);
        };
      }
    }
  }, [currentTrackId]);

  // 60fps differential reel physics loop
  useEffect(() => {
    let rafId: number;
    const tick = () => {
      const active = isPlaying && !isEjected && Boolean(track);
      if (active) {
        const p = duration > 0 ? Math.max(0.05, Math.min(0.95, currentTime / duration)) : 0.36;
        const baseSpeed = 1.65 * windMultiplier;
        // Supply reel spins faster as its tape pack shrinks; take-up reel spins slower as its pack grows
        const speedL = baseSpeed * (0.82 + p * 0.48);
        const speedR = baseSpeed * (1.28 - p * 0.48);

        angleRef.current.l = (angleRef.current.l - speedL) % 360;
        angleRef.current.r = (angleRef.current.r - speedR) % 360;

        if (leftReelRef.current) {
          leftReelRef.current.style.transform = `rotate(${angleRef.current.l.toFixed(2)}deg)`;
        }
        if (rightReelRef.current) {
          rightReelRef.current.style.transform = `rotate(${angleRef.current.r.toFixed(2)}deg)`;
        }

        const scaleL = 0.98 - p * 0.30;
        const scaleR = 0.68 + p * 0.30;
        if (leftPackRef.current) {
          leftPackRef.current.style.transform = `scale(${scaleL.toFixed(3)}) rotate(${(angleRef.current.l * 0.5).toFixed(1)}deg)`;
        }
        if (rightPackRef.current) {
          rightPackRef.current.style.transform = `scale(${scaleR.toFixed(3)}) rotate(${(angleRef.current.r * 0.5).toFixed(1)}deg)`;
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isPlaying, isEjected, windMultiplier, currentTime, duration]);

  // Update floating peak-hold memory
  useEffect(() => {
    if (!isPlaying) return;
    const timer = setInterval(() => {
      const lvlL = telemetry.rmsLeft > 0.02 ? Math.min(19, Math.max(1, Math.round(telemetry.rmsLeft * 20))) : 0;
      const lvlR = telemetry.rmsRight > 0.02 ? Math.min(19, Math.max(1, Math.round(telemetry.rmsRight * 20))) : 0;
      setPeaks((prev) => ({
        l: lvlL >= prev.l ? lvlL : Math.max(0, prev.l - 1),
        r: lvlR >= prev.r ? lvlR : Math.max(0, prev.r - 1),
      }));
    }, 95);
    return () => clearInterval(timer);
  }, [isPlaying, telemetry.rmsLeft, telemetry.rmsRight]);

  const triggerRecoil = () => {
    setIsRecoil(true);
    setTimeout(() => setIsRecoil(false), 160);
  };

  const handleFastWind = (dir: -1 | 1) => {
    tg.haptic('medium');
    triggerRecoil();
    setWindMultiplier(dir * 5.5);
    const step = dir * 15;
    onSeek(Math.max(0, Math.min(duration, currentTime + step)));
    setTimeout(() => setWindMultiplier(1), 750);
  };

  const handleScrub = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    tg.haptic('light');
    onSeek(ratio * duration);
  };

  const formatVfdClock = (sec: number) => {
    if (!sec || isNaN(sec)) return '00:00.0';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    const tenths = Math.floor((sec % 1) * 10);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${tenths}`;
  };

  const activeSegsL = isPlaying && telemetry.rmsLeft > 0.02 ? Math.min(19, Math.max(1, Math.round(telemetry.rmsLeft * 20))) : 0;
  const activeSegsR = isPlaying && telemetry.rmsRight > 0.02 ? Math.min(19, Math.max(1, Math.round(telemetry.rmsRight * 20))) : 0;

  return (
    <>
      {/* 1. VOLUMETRIC 3D CASSETTE BAY CHASSIS */}
      <section className={`cassette-bay-chassis ${isRecoil ? 'recoil' : ''}`}>
        {/* 3D Perspective Ejectable Cassette Door Stage */}
        <div
          className={`cassette-door-stage ${isEjected ? 'ejected' : ''}`}
          onClick={onOpenFullPlayer}
          title="Open Full Studio Console"
        >
          {/* Warm Incandescent Studio Lamp Behind Cassette */}
          <div className="well-backlight" />

          {/* Multi-Layer Raytraced 3D Cassette Composite */}
          <div className={`cassette-3d-composite ${isSwapping ? 'swapping' : ''}`}>
            {/* LAYER 1: Inside Cassette Well — 3D Magnetic Tape Packs & 3D Spun-Gold Open Reels */}
            <div className="cassette-3d-interior">
              {/* Angled Oxide Tape Path Lines */}
              <div className="oxide-tape-guide left" />
              <div className="oxide-tape-guide right" />

              {/* Left Supply Reel Assembly */}
              <div className="reel-3d-slot left">
                <div
                  ref={leftPackRef}
                  className="tape-pack-3d-layer"
                  style={{ transform: `scale(${(0.98 - progress * 0.3).toFixed(3)})` }}
                >
                  <img
                    src="/assets/deck/tape-pack-3d.png"
                    alt=""
                    draggable={false}
                  />
                </div>
                <div ref={leftReelRef} className="gold-reel-3d-layer">
                  <img
                    src="/assets/deck/gold-reel-3d.png"
                    alt="Supply 24K Gold Reel"
                    draggable={false}
                  />
                </div>
                {/* Stationary Specular Studio Highlight Over Rotating Reel */}
                <div className="reel-stationary-sheen" />
              </div>

              {/* Center Bitrate Reticle inside Window */}
              <div className="window-optical-gauge">
                <div className="laser-lock-pill">
                  {track
                    ? `${track.bitDepth.replace('-bit', 'B')} / ${track.sampleRate.replace('.0 kHz', 'K').replace(' kHz', 'K')}`
                    : '--B / --K'}
                </div>
              </div>

              {/* Right Take-Up Reel Assembly */}
              <div className="reel-3d-slot right">
                <div
                  ref={rightPackRef}
                  className="tape-pack-3d-layer"
                  style={{ transform: `scale(${(0.68 + progress * 0.3).toFixed(3)})` }}
                >
                  <img
                    src="/assets/deck/tape-pack-3d.png"
                    alt=""
                    draggable={false}
                  />
                </div>
                <div ref={rightReelRef} className="gold-reel-3d-layer">
                  <img
                    src="/assets/deck/gold-reel-3d.png"
                    alt="Take-Up 24K Gold Reel"
                    draggable={false}
                  />
                </div>
                <div className="reel-stationary-sheen" />
              </div>
            </div>

            {/* LAYER 2: Raytraced 3D Die-Cast Bronze & 24K Gold Cassette Shell Chassis */}
            <img
              src="/assets/deck/cassette-shell-3d.png"
              alt="Die-Cast Bronze 3D Cassette Shell"
              className="cassette-shell-3d-img"
              draggable={false}
            />

            {/* Top Recessed Foil Label Overlay (Positioned inside the 3D shell pocket) */}
            <div className="cassette-3d-label-overlay">
              <div className="label-meta-col">
                <div className="cassette-track-title">
                  {track ? track.title : 'ОЖИДАНИЕ ВЫБОРА ТРЕКА'}
                </div>
                <div className="cassette-artist-sub">
                  {track
                    ? `${track.artist.toUpperCase()} • ${track.album.toUpperCase()}`
                    : 'ВЫБЕРИТЕ ТРЕК В ПОИСКЕ ИЛИ ПРОФИЛЕ'}
                </div>
              </div>
              <span className="tape-formulation-badge">
                {track ? `${track.format} ${track.bitDepth.replace('-bit', '-БИТ')}` : 'STANDBY'}
              </span>
            </div>

            {/* Smoked Acrylic Window Glass Glare Overlay */}
            <div className="cassette-window-glass-glare" />

            {/* LAYER 3: Bottom Trapezoid Cutout — Moving Oxide Tape + 3D Crystalloy Head Block */}
            <div className="cassette-3d-head-well">
              <div className={`moving-tape-ribbon ${isPlaying && !isEjected && Boolean(track) ? 'active' : ''}`} />
              <div className={`head-assembly-3d-wrap ${isPlaying && !isEjected && Boolean(track) ? 'engaged' : ''}`}>
                <img
                  src="/assets/deck/head-assembly-3d.png"
                  alt="Discrete 3-Head Crystalloy Assembly"
                  className="head-assembly-3d-img"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. WARM TUNGSTEN / CHAMPAGNE VFD PEAK-HOLD TELEMETRY */}
      <section className="vfd-telemetry-module">
        <div className="vfd-header">
          <span className="vfd-time">{formatVfdClock(currentTime)}</span>
          <div className="vfd-badges">
            {track && (
              <button
                type="button"
                className="vfd-flac-btn"
                onClick={() => onSendFlacToBot?.(track)}
                title="Отправить FLAC в чат Telegram"
              >
                <Download size={10} />
                <span>FLAC В БОТА</span>
              </button>
            )}
            <span className="vfd-badge gold">
              {track
                ? `${track.bitDepth.replace('-bit', ' БИТ')} / ${track.sampleRate.replace('kHz', 'кГц')}`
                : '-- БИТ / -- кГц'}
            </span>
            <span className="vfd-badge">{track ? track.bitrate.toUpperCase() : '0 KBPS'}</span>
          </div>
        </div>

        <div className="vfd-channels">
          <div className="vfd-ch-row">
            <span className="vfd-ch-label">Л</span>
            <div className="vfd-segs">
              {Array.from({ length: VFD_SEGMENTS }).map((_, i) => {
                const isOn = i < activeSegsL;
                const isPeak = isPlaying && i === peaks.l;
                const toneClass =
                  i < 12 ? 'on-champagne' : i < 17 ? 'on-amber' : 'on-red';
                return (
                  <div
                    key={i}
                    className={`v-seg ${isOn ? toneClass : ''} ${isPeak ? 'peak-hold' : ''}`}
                  />
                );
              })}
            </div>
            <span className="vfd-db-val">
              {isPlaying ? `${telemetry.peakDbL.toFixed(1)} dB` : '-∞ dB'}
            </span>
          </div>

          <div className="vfd-ch-row">
            <span className="vfd-ch-label">П</span>
            <div className="vfd-segs">
              {Array.from({ length: VFD_SEGMENTS }).map((_, i) => {
                const isOn = i < activeSegsR;
                const isPeak = isPlaying && i === peaks.r;
                const toneClass =
                  i < 12 ? 'on-champagne' : i < 17 ? 'on-amber' : 'on-red';
                return (
                  <div
                    key={i}
                    className={`v-seg ${isOn ? toneClass : ''} ${isPeak ? 'peak-hold' : ''}`}
                  />
                );
              })}
            </div>
            <span className="vfd-db-val">
              {isPlaying ? `${telemetry.peakDbR.toFixed(1)} dB` : '-∞ dB'}
            </span>
          </div>
        </div>
      </section>

      {/* 3. MECHANICAL PIANO-KEY TRANSPORT CONSOLE & 4 3D KNURLED BRASS KNOBS */}
      <section className="transport-console">
        <div className="deck-waveform-glass-wrap">
          <SoundcloudWaveform
            currentTime={currentTime}
            duration={duration}
            onSeek={onSeek}
            trackId={track?.id || 'deck_master'}
            trackTitle={track?.title || ''}
            height={26}
            barWidth={2.2}
            barGap={1.4}
            showBadge={false}
          />
        </div>


        <div className="piano-keys-row">
          <button
            type="button"
            className="mech-key"
            onClick={() => {
              tg.haptic('medium');
              triggerRecoil();
              onPrev();
            }}
          >
            <PrevTapeIcon size={13} />
            <span>ПРЕД</span>
          </button>

          <button
            type="button"
            className="mech-key"
            onClick={() => handleFastWind(-1)}
          >
            <RewindIcon size={13} />
            <span>-15 С</span>
          </button>

          <button
            type="button"
            className={`mech-key play-master ${isPlaying ? 'locked' : ''}`}
            onClick={() => {
              tg.haptic('medium');
              triggerRecoil();
              if (isEjected) setIsEjected(false);
              onTogglePlay();
            }}
          >
            {isPlaying ? <PauseBarsIcon size={13} /> : <PlayWedgeIcon size={13} />}
            <span>{isPlaying ? 'ПАУЗА' : 'ПУСК'}</span>
          </button>

          <button
            type="button"
            className="mech-key"
            onClick={() => handleFastWind(1)}
          >
            <FastForwardIcon size={13} />
            <span>+15 С</span>
          </button>

          <button
            type="button"
            className="mech-key"
            onClick={() => {
              tg.haptic('medium');
              triggerRecoil();
              onNext();
            }}
          >
            <NextTapeIcon size={13} />
            <span>СЛЕД</span>
          </button>
        </div>

        {/* 4 Raytraced 3D Knurled Brass Calibration Knobs (DSP: НИЗКИЕ, ВЫСОКИЕ, БАЛАНС, ГРОМКОСТЬ) */}
        <div className="calibration-row">
          <AnalogKnob
            id="bass"
            label="НИЗКИЕ"
            displayValue={`${dsp.bassDb > 0 ? '+' : ''}${Math.round(dsp.bassDb)} дБ`}
            angle={(dsp.bassDb / 12) * 135}
            currentValue={dsp.bassDb}
            onDragChange={(startVal, totalDelta) => {
              const next = Math.max(-12, Math.min(12, Math.round(startVal + (totalDelta / 140) * 24)));
              onSetBass(next);
            }}
            onStep={() => {
              const cur = Math.round(dsp.bassDb);
              if (cur >= 12) onSetBass(0);
              else if (cur >= 0) onSetBass(Math.min(12, cur + 3));
              else onSetBass(0);
            }}
            onReset={() => onSetBass(0)}
          />

          <AnalogKnob
            id="treble"
            label="ВЫСОКИЕ"
            displayValue={`${dsp.trebleDb > 0 ? '+' : ''}${Math.round(dsp.trebleDb)} дБ`}
            angle={(dsp.trebleDb / 12) * 135}
            currentValue={dsp.trebleDb}
            onDragChange={(startVal, totalDelta) => {
              const next = Math.max(-12, Math.min(12, Math.round(startVal + (totalDelta / 140) * 24)));
              onSetTreble(next);
            }}
            onStep={() => {
              const cur = Math.round(dsp.trebleDb);
              if (cur >= 12) onSetTreble(0);
              else if (cur >= 0) onSetTreble(Math.min(12, cur + 3));
              else onSetTreble(0);
            }}
            onReset={() => onSetTreble(0)}
          />

          <AnalogKnob
            id="balance"
            label="БАЛАНС"
            displayValue={
              Math.abs(dsp.balance) < 0.05
                ? 'ЦЕНТР'
                : dsp.balance < 0
                  ? `Л ${Math.round(Math.abs(dsp.balance) * 100)}%`
                  : `П ${Math.round(dsp.balance * 100)}%`
            }
            angle={dsp.balance * 135}
            currentValue={dsp.balance}
            onDragChange={(startVal, totalDelta) => {
              let next = Math.max(-1, Math.min(1, startVal + (totalDelta / 140) * 2));
              if (Math.abs(next) < 0.04) next = 0;
              else next = Math.round(next * 20) / 20;
              onSetBalance(next);
            }}
            onStep={() => {
              const b = dsp.balance;
              if (Math.abs(b) < 0.1) onSetBalance(0.5);
              else if (b > 0.1 && b < 0.8) onSetBalance(1.0);
              else if (b >= 0.8) onSetBalance(-1.0);
              else if (b <= -0.8) onSetBalance(-0.5);
              else onSetBalance(0);
            }}
            onReset={() => onSetBalance(0)}
          />

          <AnalogKnob
            id="volume"
            label="ГРОМКОСТЬ"
            displayValue={`${Math.round(volume * 100)}%`}
            angle={-135 + volume * 270}
            currentValue={volume}
            onDragChange={(startVal, totalDelta) => {
              const next = Math.max(0, Math.min(1, Math.round((startVal + (totalDelta / 140) * 1) * 100) / 100));
              onSetVolume(next);
            }}
            onStep={() => {
              if (volume < 0.24) onSetVolume(0.25);
              else if (volume < 0.49) onSetVolume(0.50);
              else if (volume < 0.74) onSetVolume(0.75);
              else if (volume < 0.84) onSetVolume(0.85);
              else if (volume < 0.98) onSetVolume(1.00);
              else onSetVolume(0.25);
            }}
            onReset={() => onSetVolume(0.85)}
          />
        </div>
      </section>
    </>
  );
};
